// Pure validation for UserFormModal. Returns i18n keys (not messages) so it
// stays framework-free and testable; the modal translates them.
import { isValidPhoneNumber } from 'react-phone-number-input';
import type { InvestorMemberRole } from '../types/investor';

/** Which account the create form produces: a platform user or an investor Hub member. */
export type UserKind = 'platform' | 'hub';

export interface UserFormValues {
  name: string;
  lastname: string;
  email: string;
  phone: string;
  password: string;
  investorId: string;
  role: InvestorMemberRole | '';
}

export type UserFormErrors = Partial<Record<keyof UserFormValues, string>>;

interface ValidationContext {
  kind: UserKind;
  isEdit: boolean;
}

const MIN_PASSWORD_LENGTH = 8;
const EMAIL_RE = /^\S+@\S+\.\S+$/;

// Fields every mode requires.
function validateIdentity(values: UserFormValues): UserFormErrors {
  const errors: UserFormErrors = {};
  if (!values.name.trim()) errors.name = 'admin:users.validation.name';
  if (!values.lastname.trim()) errors.lastname = 'admin:users.validation.lastname';
  if (!values.email.trim() || !EMAIL_RE.test(values.email))
    errors.email = 'admin:users.validation.email';
  return errors;
}

// Platform user: phone and password required on create, optional on edit.
function validatePlatform(values: UserFormValues, isEdit: boolean): UserFormErrors {
  const errors: UserFormErrors = {};
  if (!isEdit && !values.phone) {
    errors.phone = 'admin:users.validation.phoneRequired';
  } else if (values.phone && !isValidPhoneNumber(values.phone)) {
    errors.phone = 'admin:users.validation.phone';
  }

  if (!isEdit && !values.password.trim()) {
    errors.password = 'admin:users.validation.password';
  } else if (values.password && values.password.length < MIN_PASSWORD_LENGTH) {
    errors.password = 'admin:users.validation.passwordMin';
  }
  return errors;
}

// Hub member: investor + role required; password optional (empty → magic-link invite).
function validateHub(values: UserFormValues): UserFormErrors {
  const errors: UserFormErrors = {};
  if (!values.investorId) errors.investorId = 'admin:users.validation.investorRequired';
  if (!values.role) errors.role = 'admin:users.validation.roleRequired';
  if (values.password && values.password.length < MIN_PASSWORD_LENGTH) {
    errors.password = 'admin:users.validation.passwordMin';
  }
  return errors;
}

/** Validates the form for the given mode; empty object means valid. */
export function validateUserForm(
  values: UserFormValues,
  { kind, isEdit }: ValidationContext
): UserFormErrors {
  // Hub mode only exists on create; editing always validates as a platform user.
  const modeErrors =
    kind === 'hub' && !isEdit ? validateHub(values) : validatePlatform(values, isEdit);
  return { ...validateIdentity(values), ...modeErrors };
}
